import { useEffect, useState } from "react";
import {
  Link,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import MenuAdmin from "../MenuAdmin/MenuAdmin";
import DishGalleryAdmin from "../DishGalleryAdmin/DishGalleryAdmin";
import ImageAdmin from "../ImageAdmin/ImageAdmin";
import ReservationAdmin from "../ReservationAdmin/ReservationAdmin";
import EventAdmin from "../EventAdmin/EventAdmin";
import TourismAdmin from "../TourismAdmin/TourismAdmin";
import AllergensAdmin from "../AllergensAdmin/AllergensAdmin";
import GoogleReviewsAdmin from "../GoogleReviewsAdmin/GoogleReviewsAdmin";
import SuppliersAdmin from "../SuppliersAdmin/SuppliersAdmin";
import Button from "../../components/buttonWrapper/Button.jsx";
import { signOutAdmin } from "../../utils/adminAuth";
import "./admin.css";

function Admin() {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 1024;
      setIsMobile(mobile);
      if (!mobile) {
        setMenuOpen(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobile) {
      document.body.style.overflow = "";
      return;
    }

    document.body.style.overflow = menuOpen ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen, isMobile]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 100);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogout = async () => {
    await signOutAdmin();
    navigate("/loginMagVinc", { replace: true });
  };

  return (
    <div className="admin-container">
      {isMobile && (
        <>
          <button
            className={`admin-hamburger-btn${menuOpen ? " open" : ""}`}
            aria-label="Ouvrir le menu admin"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span />
            <span />
            <span />
          </button>

          {menuOpen && (
            <div
              className="admin-burger-overlay"
              onClick={() => setMenuOpen(false)}
            />
          )}
        </>
      )}

      {/* Sidebar */}
      <aside className={`admin-sidebar${menuOpen ? " open" : ""}`}>
        <div className="sidebar-header">
          <h2>🎨 Admin Panel</h2>
          <p className="sidebar-subtitle">Carpe Diem</p>
        </div>

        <nav className="sidebar-nav">
          <Link
            to="/admin"
            className={`nav-item ${currentPath === "/admin" ? "active" : ""}`}
            onClick={() => isMobile && setMenuOpen(false)}
          >
            <span className="nav-icon">📊</span>
            <span className="nav-label">Dashboard</span>
          </Link>

          <Link
            to="/admin/menus"
            className={`nav-item ${currentPath === "/admin/menus" ? "active" : ""}`}
            onClick={() => isMobile && setMenuOpen(false)}
          >
            <span className="nav-icon">🍽️</span>
            <span className="nav-label">Menus</span>
          </Link>

          <Link
            to="/admin/photos-plats"
            className={`nav-item ${currentPath === "/admin/photos-plats" ? "active" : ""}`}
            onClick={() => isMobile && setMenuOpen(false)}
          >
            <span className="nav-icon">🖼️</span>
            <span className="nav-label">Photos des plats</span>
          </Link>

          <Link
            to="/admin/images"
            className={`nav-item ${currentPath === "/admin/images" ? "active" : ""}`}
            onClick={() => isMobile && setMenuOpen(false)}
          >
            <span className="nav-icon">🖼️</span>
            <span className="nav-label">Images</span>
          </Link>

          <Link
            to="/admin/reservations"
            className={`nav-item ${currentPath === "/admin/reservations" ? "active" : ""}`}
            onClick={() => isMobile && setMenuOpen(false)}
          >
            <span className="nav-icon">📅</span>
            <span className="nav-label">Reservations</span>
          </Link>

          <Link
            to="/admin/evenements"
            className={`nav-item ${currentPath === "/admin/evenements" ? "active" : ""}`}
            onClick={() => isMobile && setMenuOpen(false)}
          >
            <span className="nav-icon">🎉</span>
            <span className="nav-label">Evenements</span>
          </Link>

          <Link
            to="/admin/tourisme"
            className={`nav-item ${currentPath === "/admin/tourisme" ? "active" : ""}`}
            onClick={() => isMobile && setMenuOpen(false)}
          >
            <span className="nav-icon">🏔️</span>
            <span className="nav-label">Tourisme</span>
          </Link>

          <Link
            to="/admin/allergenes"
            className={`nav-item ${currentPath === "/admin/allergenes" ? "active" : ""}`}
            onClick={() => isMobile && setMenuOpen(false)}
          >
            <span className="nav-icon">⚠️</span>
            <span className="nav-label">Allergènes, fournisseur, origines</span>
          </Link>

          <Link
            to="/admin/avis-google"
            className={`nav-item ${currentPath === "/admin/avis-google" ? "active" : ""}`}
            onClick={() => isMobile && setMenuOpen(false)}
          >
            <span className="nav-icon">⭐</span>
            <span className="nav-label">Avis Google</span>
          </Link>

          <Link
            to="/admin/fournisseurs"
            className={`nav-item ${currentPath === "/admin/fournisseurs" ? "active" : ""}`}
            onClick={() => isMobile && setMenuOpen(false)}
          >
            <span className="nav-icon">🚚</span>
            <span className="nav-label">Fournisseurs</span>
          </Link>
        </nav>

        <div className="sidebar-footer">
          <Link
            to="/"
            className="btn-back-site"
            onClick={() => setMenuOpen(false)}
          >
            ← Retour au site
          </Link>
          <button
            className="btn-back-site btn-logout-admin"
            onClick={handleLogout}
          >
            Se déconnecter
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/menus" element={<MenuAdmin />} />
          <Route path="/photos-plats" element={<DishGalleryAdmin />} />
          <Route path="/images" element={<ImageAdmin />} />
          <Route path="/reservations" element={<ReservationAdmin />} />
          <Route path="/evenements" element={<EventAdmin />} />
          <Route path="/tourisme" element={<TourismAdmin />} />
          <Route path="/allergenes" element={<AllergensAdmin />} />
          <Route path="/avis-google" element={<GoogleReviewsAdmin />} />
          <Route path="/fournisseurs" element={<SuppliersAdmin />} />
        </Routes>
      </main>

      <Button visible={showScrollTop} />
    </div>
  );
}

// Dashboard principal
function Dashboard() {
  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Bienvenue dans l'administration</h1>
        <p className="dashboard-subtitle">
          Gérez le contenu de votre site Carpe Diem
        </p>
      </div>

      <div className="dashboard-cards">
        <Link to="/admin/menus" className="dashboard-card">
          <div className="card-icon">🍽️</div>
          <h3>Gestion des Menus</h3>
          <p>Créer et modifier les menus du restaurant</p>
        </Link>

        <Link to="/admin/photos-plats" className="dashboard-card">
          <div className="card-icon">🖼️</div>
          <h3>Photos des plats</h3>
          <p>
            Ajouter, retirer et faire tourner les photos visibles avant
            réservation
          </p>
        </Link>

        <Link to="/admin/images" className="dashboard-card">
          <div className="card-icon">🖼️</div>
          <h3>Gestion des Images</h3>
          <p>Modifier toutes les images du site</p>
        </Link>

        <Link to="/admin/reservations" className="dashboard-card">
          <div className="card-icon">📅</div>
          <h3>Reservations</h3>
          <p>Consulter et traiter les demandes de reservation clients</p>
        </Link>

        <Link to="/admin/evenements" className="dashboard-card">
          <div className="card-icon">🎉</div>
          <h3>Evenements</h3>
          <p>Gerer les événements affichés sur l accueil et la page dédiée</p>
        </Link>

        <Link to="/admin/tourisme" className="dashboard-card">
          <div className="card-icon">🏔️</div>
          <h3>Tourisme</h3>
          <p>Gérer les activités et lieux touristiques</p>
        </Link>

        <Link to="/admin/allergenes" className="dashboard-card">
          <div className="card-icon">⚠️</div>
          <h3>Allergènes</h3>
          <p>Gérer le tableau des allergènes des plats</p>
        </Link>

        <Link to="/admin/fournisseurs" className="dashboard-card">
          <div className="card-icon">🚚</div>
          <h3>Fournisseurs</h3>
          <p>Ajouter et gérer la liste des fournisseurs partenaires</p>
        </Link>

        <div className="dashboard-card info-card">
          <div className="card-icon">ℹ️</div>
          <h3>À propos</h3>
          <p>
            Ce panneau d'administration vous permet de gérer facilement le
            contenu de votre site. Les modifications sont automatiquement
            enregistrées.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Admin;
